import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import ConfirmDeleteModal from "../../components/confirmDeleteModal";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiCheck,
  FiX,
  FiRefreshCw,
} from "react-icons/fi";
import { API_BASE } from "../../config/apiConfig";
import {
  setReasons,
  addReason,
  updateReason,
  removeReason,
} from "../../features/coachLeaves/coachLeaveReasonsSlice";

export default function CoachLeaveReasonsPage() {
  const token = sessionStorage.getItem("access_token");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const reasons = useSelector((state) => state.coachLeaveReasons.items);
  const loaded = useSelector((state) => state.coachLeaveReasons.loaded);
  const [newReason, setNewReason] = useState("");
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchReasons(false);
  }, []);

  async function fetchReasons(force = false) {
    // if already loaded and not forcing → do nothing
    if (!force && loaded) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-reasons/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) dispatch(setReasons(data.reasons || []));
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!newReason.trim()) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-reasons/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label: newReason }),
      });

      const data = await res.json();
      if (data.success) {
        dispatch(addReason({ id: data.id, label: newReason }));
        setNewReason("");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveEdit(id) {
    if (!editLabel.trim()) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-reasons/update/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label: editLabel }),
      });
      
      const data = await res.json();
      if (data.success) {
        dispatch(updateReason({ id, label: editLabel }));
        setEditing(null);
      }
    } finally {
      setLoading(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leave-reasons/delete/${deleteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = await res.json();
      if (data.success) {
        dispatch(removeReason(deleteId));
        setDeleteId(null);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <MainContentLayout>
        {/* PAGE HEADER */}
        <PageHeader title="Coach Leave Reasons" backTo="/dashboard" />
        <LoadingOverlay show={loading} />

        {/* Refresh button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => fetchReasons(true)}
            className="p-2 text-blue-600 hover:text-blue-800 transition rounded"
            title="Refresh"
          >
            <FiRefreshCw size={20} />
          </button>
        </div>

      <div className="mb-4 flex gap-3">
        <input
          type="text"
          value={newReason}
          onChange={(e) => setNewReason(e.target.value)}
          placeholder="New reason"
          className="border rounded p-2 flex-grow placeholder:italic placeholder:text-gray-400"
        />
        <button
          onClick={handleAdd}
          disabled={newReason.trim().length === 0 }
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiPlus size={18} />
        </button>
      </div>

      <ul className="space-y-2 mt-6">
        {reasons.map((r, index) => (
          <li
            key={r.id}
            className="flex justify-between items-center border-b py-2"
          >
            {editing === r.id ? (
              <>
                <input
                  type="text"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  className="border rounded p-2 flex-grow mr-2"
                />
                <button
                  onClick={() => handleSaveEdit(r.id)}
                  className="p-2 bg-green-600 text-white rounded"
                >
                  <FiCheck />
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="p-2 bg-gray-400 text-white rounded ml-2"
                >
                  <FiX />
                </button>
              </>
            ) : (
              <>
                <span>{index + 1}. {r.label}</span>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setEditing(r.id);
                      setEditLabel(r.label);
                    }}
                    className="p-2 bg-yellow-500 text-white rounded"
                  >
                    <FiEdit />
                  </button>

                  <button
                    onClick={() => setDeleteId(r.id)}
                    className="p-2 bg-red-600 text-white rounded"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>

      {deleteId && (
        <ConfirmDeleteModal
          show={true}
          title="Delete Reason"
          message="Delete this reason?"
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      )}
    </MainContentLayout>
  );
}
